import { documentWriteError } from "./documentWriteError";
import { apiUrl } from "../utils/api";

export interface DocumentDto {
    id: number;
    fileName: string;
    mimeType?: string | null;
    fileSize?: number | null;
    category?: string | null;
    confidential?: boolean | null;
    tags?: string[];
    uploadedAt?: string | null;
    /**
     * SHA-256 der Bytes, beim Upload gesetzt (Liste und Einzelabruf liefern es
     * mit). Der Verlauf erkennt daran ein Dokument, dessen Inhalt schon als
     * Bild in einer Notiz steht (`notizbildDoppel.ts`). Null bei Altbestand.
     */
    contentHash?: string | null;
    /**
     * Wer hochgeladen hat — better-auth-User-Id, über die Nutzerliste zum Namen
     * aufzulösen (PO 14.09.2026: Dokument-Karte „oben von wem und wann"). Null
     * bei Bestand vor der Spalte und bei Läufen ohne Request (Sync, Scheduler).
     */
    uploadedByUserId?: string | null;
    contactId?: number | null;
    /**
     * Nur `GET /documents/:id`: der Kunde des Dokuments, abgeleitet aus
     * Vertrag/Schaden/Risiko/Vorgang, wenn es keinen eigenen Kontakt trägt
     * (T-41). Die Listen liefern das Feld nicht.
     */
    kundeId?: number | null;
    contractId?: number | null;
    damageId?: number | null;
    riskObjectId?: number | null;
    todoId?: number | null;
    vorgangId?: number | null;
    calendarTerminId?: number | null;
    downloadUrl?: string | null;
    /** Volltext-Fundstellen-Snippet mit [[…]]-Markern (Backend, Task 7). */
    snippet?: string;
    /**
     * P6 (PO 14.09.2026): Herkunfts-Vorgang — direkt (`vorgangId`) oder über
     * das To-do. Nur `GET /documents` (Liste) liefert das Feld; die Kundenakte
     * zeigt daraus das Tag „aus Vorgang <Nr>" und springt per `id` in die
     * Vorgangsansicht. `null`/fehlend = kein Vorgangsbezug.
     */
    vorgang?: { id: number; vorgangNbr: string | null; titel: string | null } | null;
    /**
     * Gesetzt, wenn der ANGEFRAGTE Vorgang dieses Dokument nur zusätzlich
     * anzeigt (PO 22.09.2026) — es also anderswo zu Hause ist. `vorgangId` und
     * `todoId` tragen dann die Sicht dieses Vorgangs, nicht die der Heimat.
     *
     * `heimat` ist der Vorgang, aus dem es stammt, oder `null` bei einem
     * Dokument aus der Kundenakte. Nur `GET /documents?vorgangId=…` liefert
     * das Feld; überall sonst fehlt es, und die Zeile ist eine gewöhnliche.
     */
    gast?: {
        heimat: { id: number; vorgangNbr: string | null; titel: string | null } | null;
    } | null;
    /**
     * Nur `GET /documents?vorgangId=…` (User 07.10.2026): das Dokument trägt
     * Fassungen (`bearbeitet`), und so hieß es früher (`fruehereNamen`). Damit
     * bleibt eine unter neuem Namen gespeicherte Datei im Vorgang EINE Datei
     * mit ihrer gleichnamigen Kopie aus einer anderen Mail.
     */
    bearbeitet?: boolean;
    fruehereNamen?: string[];
    /**
     * Papierkorb (PO 16.09.2026): Zeitpunkt des Löschens als ISO-Text, `null`
     * bei einem aktiven Dokument. Gefüllt nur, wenn die Liste ausdrücklich
     * gelöschte Dokumente angefordert hat (`papierkorb`) — sonst ist alles,
     * was zurückkommt, ohnehin aktiv.
     */
    deleted?: string | null;
}

export type DocumentFilter = {
    contactId?: number;
    contractId?: number;
    damageId?: number;
    riskObjectId?: number;
    todoId?: number;
    vorgangId?: number;
    calendarTerminId?: number;
    search?: string;
    category?: string;
    /**
     * J6/A7 — Kategorien ausschließen (Array; geht komma-separiert an die API).
     * Die Mail-Leiste blendet damit standardmäßig die „Alte Kundenakte" aus.
     */
    excludeCategory?: string[];
    limit?: number;
    offset?: number;
    /**
     * Explorer-Modus (Alte Kundenakten): begrenzt die Treffer auf Dateien DIREKT
     * in diesem Ordner. Der Wert ist der aktuelle Ordnerpfad ("" = Wurzel, sonst
     * auf "/" endend). Das Backend matcht `fileName LIKE :prefix || '%'` und
     * schließt Dateien in Unterordnern (`… LIKE :prefix || '%/%'`) aus.
     */
    pathPrefix?: string;
    /** Alle Dateien unter diesem Ordner, auch aus Unterordnern (Ordner herunterladen). */
    pathUnder?: string;
    /**
     * Sortierfeld der Explorer-Dateiliste: "name" | "size" | "date". Ohne
     * Angabe bleibt es beim Server-Default (uploadedAt absteigend), damit
     * bestehende Aufrufer unverändert bleiben. Das Backend prüft gegen eine
     * Whitelist.
     */
    sort?: "name" | "size" | "date";
    /** Sortierrichtung; ohne Angabe: "date" absteigend, sonst aufsteigend. */
    dir?: "asc" | "desc";
    /**
     * P6 (PO 14.09.2026): NUR die Dokumente, die über einen Vorgang bzw. ein
     * To-do dem Kontakt zugerechnet werden — die Explorer-Wurzel der
     * Kundenakte zeigt sie in einer eigenen Gruppe „Vorgänge". Wirkt nur
     * zusammen mit `contactId`.
     */
    nurVorgangsdokumente?: boolean;
    /**
     * Das Gegenstück: die Akte des Kunden OHNE seine Vorgangsdokumente. Die
     * beiden KMVS-Reiter der Dokument-Auswahl teilen den Bestand damit
     * überschneidungsfrei auf. Wirkt nur zusammen mit `contactId`; zusammen
     * mit `nurVorgangsdokumente` antwortet der Server mit 400.
     */
    ohneVorgangsdokumente?: boolean;
    /**
     * Papierkorb (PO 16.09.2026). Ohne Angabe liefert der Server nur aktive
     * Dokumente — jeder bestehende Aufrufer bleibt also unverändert.
     * `"geloescht"` ist der Schalter „Gelöschte anzeigen", `"alle"` zeigt
     * beides.
     */
    papierkorb?: "aktiv" | "geloescht" | "alle";
};

/** Ein unmittelbarer Unterordner auf der aktuellen Ebene (GET /documents/folders). */
export interface DocumentFolder {
    /** Erstes Pfadsegment nach dem Prefix. */
    name: string;
    /** Anzahl Dokumente (rekursiv) unter diesem Unterordner. */
    count: number;
}

export interface DocumentPage {
    items: DocumentDto[];
    total: number;
}

export interface SignedDownloadLink {
    documentId: number;
    expiresAt: number;
    signature: string;
    tenantId: string;
    /** API-relative URL der `@Public()`-signierten Download-Route. */
    url: string;
}

/**
 * Fehlgeschlagener Upload **mit** HTTP-Status. Bis hierher steckte der Status
 * nur im Meldungstext (`Upload failed (413): …`) und war für den Aufrufer nicht
 * auswertbar — 413 (zu groß), 400 (gesperrte Endung), 429 (Rate-Limit) und 404
 * (Risikoobjekt nicht gefunden) brauchen aber unterschiedliche Reaktionen.
 */
export class UploadError extends Error {
    readonly status: number;
    /**
     * Sekunden bis zur nächsten erlaubten Anfrage, aus `RateLimit-Reset` bzw.
     * `Retry-After` der Antwort (express-rate-limit `standardHeaders`). Nur bei
     * 429 gesetzt — ein fixer Retry-Abstand reicht nicht, weil der
     * Upload-Bucket **pro IP** gilt und hinter dem zentralen Proxy von allen
     * Nutzern geteilt wird.
     */
    readonly retryAfterSeconds: number | null;
    /**
     * Woran es lag, wenn kein HTTP-Status da ist: `nicht-lesbar` = der Browser
     * konnte die Datei nicht lesen (gesperrt/geändert), `verbindung` = das
     * Senden ist gescheitert. Sonst `null`.
     */
    readonly grund: "nicht-lesbar" | "verbindung" | null;

    constructor(
        status: number,
        message: string,
        retryAfterSeconds: number | null = null,
        grund: "nicht-lesbar" | "verbindung" | null = null,
    ) {
        super(message);
        this.name = "UploadError";
        this.status = status;
        this.retryAfterSeconds = retryAfterSeconds;
        this.grund = grund;
    }
}

/** Größte Datei für `POST /documents/upload` — gleich dem Backend-Limit
 *  (DOCUMENT_FILE_SIZE_LIMIT_BYTES) und der nginx-Regel für diesen Pfad. */
export const DOKUMENT_MAX_BYTES = 200 * 1024 * 1024;

/** „250 MB“, „3,4 MB“ — für Meldungen an Nutzer. */
export const alsMegabyte = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb >= 10 ? Math.round(mb) : mb.toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
};

/** Deutsche Kurzmeldung je Status — die Rohantwort ist für Endnutzer unlesbar. */
export const uploadFehlerText = (status: number): string => {
    if (status === 413) return `Die Datei ist zu groß (höchstens ${alsMegabyte(DOKUMENT_MAX_BYTES)}).`;
    if (status === 415) return "Dieser Dateityp wird nicht akzeptiert.";
    if (status === 400) return "Die Datei wurde abgelehnt (gesperrter Dateityp?).";
    if (status === 404) return "Das Risikoobjekt wurde nicht gefunden.";
    if (status === 429) return "Zu viele Uploads in kurzer Zeit.";
    if (status === 401 || status === 403) return "Keine Berechtigung zum Hochladen.";
    // T-74: 503 kommt vom Server selbst (Dateispeicher antwortet nicht) —
    // dann ist sicher nichts gespeichert.
    if (status === 503) {
        return "Der Dateispeicher antwortet gerade nicht. Bitte in ein paar Minuten noch einmal versuchen.";
    }
    // 502/504 kommen vom Proxy davor: Der Server kann danach weitergearbeitet
    // und die Datei doch noch abgelegt haben — deshalb der Prüf-Hinweis.
    if (status === 502 || status === 504) {
        return "Der Server hat zu lange gebraucht oder der Dateispeicher ist gerade ausgelastet. Bitte in ein paar Minuten noch einmal versuchen. Prüfen Sie vorher, ob die Datei nicht doch schon angehängt ist.";
    }
    return `Upload fehlgeschlagen (${status}).`;
};

/** `RateLimit-Reset`/`Retry-After` in Sekunden, sofern vorhanden und plausibel. */
const retryAfterFromHeaders = (headers: Headers): number | null => {
    for (const name of ["retry-after", "ratelimit-reset", "x-ratelimit-reset"]) {
        const raw = headers.get(name);
        if (!raw) continue;
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0 && n <= 3600) return n;
    }
    return null;
};

const buildQuery = (filter: DocumentFilter): string => {
    const qs = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        // Listen gehen komma-separiert raus (`excludeCategory`); ein leeres
        // Array bedeutet „kein Filter" und wird gar nicht gesendet.
        if (Array.isArray(v)) {
            const joined = v.map((x) => String(x ?? "").trim()).filter(Boolean).join(",");
            if (joined) qs.append(k, joined);
            return;
        }
        qs.append(k, String(v));
    });
    const s = qs.toString();
    return s ? `?${s}` : "";
};

/** Meldung, wenn der Browser eine Datei nicht lesen kann. */
export const dateiNichtLesbarText = (name: string): string =>
    `Die Datei „${name}“ ließ sich nicht lesen – ist sie noch in einem anderen Programm geöffnet? Bitte schließen und erneut versuchen.`;

/**
 * Die Datei vollständig in den Speicher lesen und als unveränderliche Kopie
 * (gleicher Name, Typ, Änderungsdatum) zurückgeben. Wirft {@link UploadError}
 * mit Status 0 und {@link dateiNichtLesbarText}, wenn das Lesen scheitert
 * (gesperrt, geändert, virtuell und nicht mehr verfügbar).
 */
export async function dateiEinlesen(file: File): Promise<File> {
    let inhalt: ArrayBuffer;
    try {
        inhalt = await file.arrayBuffer();
    } catch {
        throw new UploadError(0, dateiNichtLesbarText(file.name), null, "nicht-lesbar");
    }
    return new File([inhalt], file.name, { type: file.type, lastModified: file.lastModified });
}

export const documentService = {
    /**
     * Ein einzelnes Dokument. Der Server prüft Mandant und Sichtbarkeit und
     * antwortet mit 404, wenn beides nicht passt — hier gibt es nichts zu
     * ergänzen.
     */
    async getOne(id: number, signal?: AbortSignal): Promise<DocumentDto> {
        const res = await fetch(apiUrl(`documents/${id}`), { signal });
        if (!res.ok) throw new Error(`Document ${id} failed: ${res.status}`);
        return res.json();
    },

    async list(filter: DocumentFilter): Promise<DocumentDto[]> {
        const res = await fetch(apiUrl(`documents${buildQuery(filter)}`));
        if (!res.ok) throw new Error(`Documents list failed: ${res.status}`);
        return res.json();
    },

    // Paged variant: pass `limit`/`offset` and read the unrestricted total from
    // the `X-Total-Count` header (set by the backend). The response body stays a
    // bare array, so `list()` above keeps working unchanged.
    async listPaged(filter: DocumentFilter, signal?: AbortSignal): Promise<DocumentPage> {
        const res = await fetch(apiUrl(`documents${buildQuery(filter)}`), { signal });
        if (!res.ok) throw new Error(`Documents list failed: ${res.status}`);
        const items: DocumentDto[] = await res.json();
        const header = res.headers.get("X-Total-Count") ?? res.headers.get("x-total-count");
        const total = header != null ? Number(header) || 0 : items.length;
        return { items, total };
    },

    // Explorer-Modus (Alte Kundenakten): die UNMITTELBAREN Unterordner auf der
    // Ebene `prefix` ("" = Wurzel, sonst auf "/" endend). Aufsteigend nach name
    // sortiert; `count` = Anzahl Dokumente (rekursiv) unter dem Unterordner.
    async listFolders(
        params: {
            contactId: number;
            prefix: string;
            category?: string;
            /** Papierkorb — muss zur Dateiliste passen, sonst stimmt der Ordner-Zähler nicht. */
            papierkorb?: "aktiv" | "geloescht" | "alle";
        },
        signal?: AbortSignal,
    ): Promise<DocumentFolder[]> {
        const qs = new URLSearchParams();
        qs.append("contactId", String(params.contactId));
        qs.append("prefix", params.prefix);
        if (params.category) qs.append("category", params.category);
        if (params.papierkorb) qs.append("papierkorb", params.papierkorb);
        const res = await fetch(apiUrl(`documents/folders?${qs.toString()}`), { signal });
        if (!res.ok) throw new Error(`Folders list failed: ${res.status}`);
        return res.json();
    },

    // Einzel-Fetch (GET /documents/:id) für Deep-Links (⌘K-Treffer, Task 10):
    // holt ein Dokument unabhängig davon, ob es auf der aktuell geladenen
    // Listen-Seite liegt oder vom lokalen Filter ausgeblendet wird.
    async fetchOne(id: number, signal?: AbortSignal): Promise<DocumentDto> {
        const res = await fetch(apiUrl(`documents/${id}`), { signal });
        if (!res.ok) throw new Error(`Document fetch failed: ${res.status}`);
        return res.json();
    },

    // Authenticated download. The `/documents/:id/download` route is guarded by
    // `documents:read`, so a plain <a href> (native navigation, no Authorization
    // header) would 401. Fetching through the patched global `fetch` attaches the
    // Bearer token; the caller turns the Blob into an object URL for save/preview.
    async fetchBlob(id: number): Promise<Blob> {
        const res = await fetch(apiUrl(`documents/${id}/download`));
        if (!res.ok) throw new Error(`Download failed: ${res.status}`);
        return res.blob();
    },

    async upload(params: {
        file: File;
        todoId?: number;
        vorgangId?: number;
        calendarTerminId?: number;
        contactId?: number;
        contractId?: number;
        damageId?: number;
        riskObjectId?: number;
        category?: string;
        confidential?: boolean;
        tags?: string[];
        /**
         * Ordner innerhalb der Alten Kundenakte, in den die Datei gehört
         * (`Vertraege/2019`). Der Explorer im Reiter „Dokumente" baut seinen
         * Baum aus `Document.fileName`; ohne diesen Pfad läge eine in einem
         * offenen Ordner hochgeladene Datei sichtbar an der Wurzel. Leer/
         * weggelassen = Wurzel, das bisherige Verhalten jedes Aufrufers.
         */
        relativePath?: string;
    }): Promise<DocumentDto> {
        // Vorab prüfen: eine zu große Datei gar nicht erst schicken — der Server
        // lehnte sie sonst nach dem ganzen Hochladen mit 413 ab (T-16).
        if (params.file.size > DOKUMENT_MAX_BYTES) {
            throw new UploadError(
                413,
                `„${params.file.name}“ ist ${alsMegabyte(params.file.size)} groß, erlaubt sind höchstens ${alsMegabyte(DOKUMENT_MAX_BYTES)}.`,
            );
        }
        // Erst vollständig einlesen, dann senden (DEV 01.11.2026, Edge/Windows:
        // „2026-10-14.log“ scheiterte als „Keine Verbindung zum Server“, nginx
        // sah `400 0`, das Backend nie etwas). Eine Datei, die ein anderes
        // Programm noch schreibt oder sperrt, oder eine virtuelle Datei (aus
        // einem ZIP, aus Outlook) lässt den Browser das Senden mittendrin
        // abbrechen. Eingelesen ändert sie sich nicht mehr — und scheitert das
        // Lesen, sagt die Meldung, woran es liegt.
        const datei = await dateiEinlesen(params.file);
        const fd = new FormData();
        fd.append("file", datei);
        if (params.todoId != null) fd.append("todoId", String(params.todoId));
        if (params.vorgangId != null) fd.append("vorgangId", String(params.vorgangId));
        if (params.calendarTerminId != null) fd.append("calendarTerminId", String(params.calendarTerminId));
        if (params.contactId != null) fd.append("contactId", String(params.contactId));
        if (params.contractId != null) fd.append("contractId", String(params.contractId));
        if (params.damageId != null) fd.append("damageId", String(params.damageId));
        if (params.riskObjectId != null) fd.append("riskObjectId", String(params.riskObjectId));
        if (params.category) fd.append("category", params.category);
        if (params.confidential != null) fd.append("confidential", String(params.confidential));
        if (params.tags && params.tags.length) fd.append("tags", JSON.stringify(params.tags));
        if (params.relativePath) fd.append("relativePath", params.relativePath);

        let res: Response;
        try {
            res = await fetch(apiUrl("documents/upload"), {
                method: "POST",
                body: fd,
            });
        } catch {
            // Verbindungsabbruch/DNS/Proxy weg: `fetch` rejectet mit einem
            // TypeError, dessen Meldung der Browser stellt („Failed to fetch",
            // in Safari „Load failed"). Der Aufrufer zeigt `err.message`
            // ungefiltert an — ohne diese Umhüllung stünde englischer
            // Browsertext im deutschen Hinweis.
            throw new UploadError(0, "Keine Verbindung zum Server.", null, "verbindung");
        }
        if (!res.ok) {
            // Body nur noch lesen, um ihn nicht offen zu lassen — die Meldung
            // für den Nutzer kommt aus dem Status, nicht aus dem Rohtext.
            const error = await documentWriteError(res, uploadFehlerText(res.status));
            throw new UploadError(
                res.status,
                error.message,
                res.status === 429 ? retryAfterFromHeaders(res.headers) : null,
            );
        }
        return res.json();
    },

    /**
     * Leeres Word- (`docx`) oder Excel-Dokument (`xlsx`) anlegen — der Server
     * erzeugt die Datei und hängt die Endung selbst an (User 28.09.2026).
     * Danach öffnet der Aufrufer den Office-Editor.
     */
    async createOffice(params: {
        art: "docx" | "xlsx";
        name: string;
        contactId?: number | null;
        vorgangId?: number | null;
        todoId?: number | null;
    }): Promise<DocumentDto> {
        const body: Record<string, unknown> = { art: params.art, name: params.name };
        if (params.contactId != null) body.contactId = params.contactId;
        if (params.vorgangId != null) body.vorgangId = params.vorgangId;
        if (params.todoId != null) body.todoId = params.todoId;
        let res: Response;
        try {
            res = await fetch(apiUrl("documents/office/neu"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
        } catch {
            throw new UploadError(0, "Keine Verbindung zum Server.");
        }
        if (!res.ok) {
            await res.text().catch(() => "");
            const text =
                res.status === 400
                    ? "Bitte einen gültigen Namen eingeben."
                    : res.status === 401 || res.status === 403
                      ? "Keine Berechtigung, hier ein Dokument anzulegen."
                      : res.status === 404
                        ? "Kontakt oder Vorgang wurde nicht gefunden."
                        : `Anlegen fehlgeschlagen (${res.status}).`;
            throw new UploadError(res.status, text);
        }
        return res.json();
    },

    // Patch bestehender Metadaten. Aktuell genutzt zum nachträglichen Umbenennen
    // (`fileName`): Das Backend erzwingt die Extension des Bestands, erhält den
    // Legacy-Verzeichnisteil und rührt `storagePath`/S3-Objekt nicht an. Ein
    // leerer/zu langer Name kommt als 400 zurück.
    async update(
        id: number,
        patch: {
            fileName?: string;
            category?: string | null;
            confidential?: boolean;
            tags?: string[];
            /** Nachträgliche Zuordnung zu einem Risikoobjekt (mandantengeprüft). */
            riskObjectId?: number | null;
            /**
             * PO 31.08.2026: Zuordnung zu einem To-do (Drag & Drop) setzen oder
             * mit `null` lösen. Der PUT-Body des Controllers prüft, dass das
             * To-do zum Mandanten gehört. Der Bezug zum Vorgang bleibt
             * unberührt — solange `vorgangId` nicht mitgeschickt wird.
             */
            todoId?: number | null;
            /**
             * PO 14.09.2026: „Aus dem Vorgang entfernen“ per Rechtsklick.
             * Entfernen heißt LÖSEN, nie löschen — `null` nimmt den direkten
             * Vorgangsbezug, die Datei bleibt. Ein gesetzter Wert hängt das
             * Dokument an einen Vorgang (mandantengeprüft); „→ zurück an den
             * Vorgang“ schickt ihn mit, damit ein im To-do hochgeladenes
             * Dokument (nur `todoId`) nicht aus dem Vorgang fällt.
             */
            vorgangId?: number | null;
            /**
             * Der Kunde, bei dem ein gelöstes Dokument bleibt. Bis zum
             * 14.09.2026 fehlte das Feld hier bewusst (der Controller kannte
             * es nicht und hätte es still verschluckt); seitdem nimmt der
             * PUT-Body es an und prüft den Mandanten. V4-Uploads tragen keinen
             * Kontakt — ohne ihn wäre ein gelöstes Dokument bezugslos.
             */
            contactId?: number | null;
            /**
             * Verschieben in der Alten Kundenakte (Drag & Drop im Explorer):
             * Zielordner relativ zur Akte, `""` = Wurzel. Nur der
             * Verzeichnisteil von `fileName` wechselt.
             */
            ordner?: string;
        },
    ): Promise<DocumentDto> {
        const res = await fetch(apiUrl(`documents/${id}`), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
        });
        if (!res.ok) {
            throw await documentWriteError(res, "Das Dokument konnte nicht geändert werden.");
        }
        return res.json();
    },

    /**
     * Löschen = in den Papierkorb legen (PO 16.09.2026). Die Datei bleibt im
     * Speicher liegen und lässt sich über `restore` zurückholen. Endgültiges
     * Löschen gibt es nur für Administratoren und nur über die API
     * (`POST /documents/:id/purge`) — bewusst NICHT von hier aus.
     */
    async remove(id: number): Promise<void> {
        const res = await fetch(apiUrl(`documents/${id}`), { method: "DELETE" });
        if (!res.ok) throw await documentWriteError(res, "Das Dokument konnte nicht gelöscht werden.");
    },

    /** Aus dem Papierkorb zurückholen. */
    async restore(id: number): Promise<void> {
        const res = await fetch(apiUrl(`documents/${id}/restore`), { method: "POST" });
        if (!res.ok) throw await documentWriteError(res, "Das Dokument konnte nicht wiederhergestellt werden.");
    },

    downloadUrl(id: number): string {
        return apiUrl(`documents/${id}/download`);
    },

    // Holt (authentifiziert, Bearer via fetch-Wrapper) eine **signierte** Download-URL.
    // Die Route `/documents/:id/download-link` ist mit `documents:sign` geschützt und
    // liefert eine HMAC-signierte URL auf die `@Public()`-Route `/download/signed`, die
    // sich selbst über die Signatur authentifiziert — ohne Cookie und ohne Bearer.
    // So bleibt der Download nach der Bearer-only-Umstellung des Guards funktionsfähig.
    async createSignedDownloadLink(id: number): Promise<SignedDownloadLink> {
        const res = await fetch(apiUrl(`documents/${id}/download-link`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
        });
        if (!res.ok) throw new Error(`Signed link failed: ${res.status}`);
        return res.json();
    },

    // Startet den Download über die signierte URL: zuerst (authentifiziert) die
    // Signatur holen, dann die selbst-authentifizierende `@Public()`-URL in einem
    // neuen Tab öffnen. Ersetzt das nackte `<a href=".../download">`, das per
    // Browser-Navigation keinen Authorization-Header trägt und am Cookie hinge.
    async openSignedDownload(id: number): Promise<void> {
        window.open(await this.signedUrl(id), "_blank", "noopener,noreferrer");
    },

    /**
     * PO 30.08.2026: die Datei IM BROWSER ansehen (neuer Tab) statt sie
     * herunterzuladen — für PDFs der übliche Weg.
     *
     * Ob das tatsächlich passiert, entscheidet der SERVER: `?inline=1` ist nur
     * ein Wunsch, und `Content-Disposition: inline` bekommt ausschließlich, was
     * der Browser als Daten darstellt (PDF, Raster-Bilder — siehe
     * `document-inline.util.ts` im Backend). Alles andere lädt weiterhin
     * herunter; der Tab schließt sich dann von selbst.
     */
    async openSignedInline(id: number): Promise<void> {
        window.open(await this.signedUrl(id, { inline: true }), "_blank", "noopener,noreferrer");
    },

    /** Absolute, selbst-authentifizierende Download-URL (optional inline). */
    async signedUrl(id: number, opts?: { inline?: boolean }): Promise<string> {
        const link = await this.createSignedDownloadLink(id);
        // link.url ist API-relativ ("/api/documents/..."); zu absoluter Origin-URL machen.
        const absolute = link.url.startsWith("http")
            ? link.url
            : `${window.location.origin}${link.url.startsWith("/") ? "" : "/"}${link.url}`;
        if (!opts?.inline) return absolute;
        return `${absolute}${absolute.includes("?") ? "&" : "?"}inline=1`;
    },
};
